'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { Separator, toast } from '@mokoboko/components';

import { useIsEmbed } from '@/contexts/embed';
import { getClassesByIds } from '@/helpers/class';
import { openSitePage } from '@/helpers/embed-navigation';
import { toPlainText } from '@/lib/plain-text';
import useAdminUsers, { AdminUser } from '@/state/admin-users';
import useClassGroups from '@/state/class-groups';
import { INSTRUCTOR_PARAM, InstructorBioPanel } from './instructor-bio-panel';
import { InstructorPill } from './instructor-pill';
import { InstructorSection } from './instructor-section';
import { ShowMoreLink } from './show-more-link';

const VISIBLE_LIMIT = 4;

export const MeetYourInstructorsSection = ({
  sitePath,
}: {
  // Where this section lives on the studio's own site. An embed cannot show the
  // bio in place — a modal is out of flow, so the frame never grows to fit it
  // and it centres on the frame's full height, far below what the visitor sees.
  sitePath: string;
}) => {
  const searchParams = useSearchParams();
  const isEmbed = useIsEmbed();
  const [expanded, setExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState(() =>
    searchParams.get(INSTRUCTOR_PARAM)
  );
  const { data } = useAdminUsers({ role: 'INSTRUCTOR' });
  const { data: classGroups } = useClassGroups({ include_classes: true });

  // API returns every instructor who may teach (ACTIVE or INVITED, signed in
  // or not) and has a bookable class, none paused or deleted — no client-side
  // status filter needed.
  const instructors = (data ?? []).filter(
    (instructor) => (instructor.class_ids?.length ?? 0) > 0
  );

  if (instructors.length === 0) return null;

  const selected =
    instructors.find((instructor) => instructor.admin_user_id === selectedId) ??
    null;

  const handleInstructorClick = (instructor: AdminUser) => {
    if (!isEmbed) {
      setSelectedId(instructor.admin_user_id);
      return;
    }

    if (
      !openSitePage(
        `${sitePath}?${INSTRUCTOR_PARAM}=${instructor.admin_user_id}`
      )
    ) {
      toast.info('Allow pop-ups to open this instructor.');
    }
  };

  const bioPanel = (
    <InstructorBioPanel
      instructor={selected}
      classes={getClassesByIds(classGroups, selected?.class_ids)}
      onClose={() => setSelectedId(null)}
    />
  );

  // A sole instructor with a bio gets the class page's section, the name over
  // the bio and photo, rather than one pill under a heading for a team.
  const sole = instructors.length === 1 ? instructors[0] : null;
  const soleBio = toPlainText(sole?.bio);
  if (sole && soleBio) {
    return (
      <div className="flex flex-col gap-6">
        <Separator />
        <InstructorSection
          instructor={sole}
          bio={soleBio}
          onOpen={() => handleInstructorClick(sole)}
        />
        {bioPanel}
      </div>
    );
  }

  const visibleInstructors = expanded
    ? instructors
    : instructors.slice(0, VISIBLE_LIMIT);
  const hasMore = instructors.length > VISIBLE_LIMIT;

  const heading =
    instructors.length === 1 ? 'Meet your instructor' : 'Meet your instructors';

  return (
    <div className="flex flex-col gap-6">
      <Separator />
      <section className="flex flex-col gap-3">
        <h2 className="heading-section">{heading}</h2>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2.5">
            {visibleInstructors.map((instructor) => (
              <InstructorPill
                key={instructor.admin_user_id}
                instructor={instructor}
                onClick={() => handleInstructorClick(instructor)}
              />
            ))}
          </div>

          {hasMore ? (
            <ShowMoreLink
              expanded={expanded}
              hiddenCount={instructors.length - VISIBLE_LIMIT}
              onToggle={() => setExpanded((value) => !value)}
            />
          ) : null}
        </div>
      </section>

      {bioPanel}
    </div>
  );
};
